import { create } from 'zustand'
import type {
  ConversationState,
  Branch,
  Message,
  Question,
  Answer,
  AnswerStatus,
  AAISuggestion,
  AAIState,
  AAIQuestionSlot,
  ProblemOutput,
} from '@/types/conversation'
import type { DiffChange } from '@/types/api'

/**
 * Conversation store — single source of truth for a project's live
 * conversation state. Field names align with the camelCase shape emitted
 * by the backend SSE events and the Convex schema.
 *
 * Action naming convention:
 *   set<Field>   — replace the field
 *   update<Thing>— partial update of a complex object
 *   add<Thing>   — append to a collection
 *   remove<Thing>— remove from a collection
 *   clear<Thing> — reset a single field
 *   reset()      — reset the whole store
 */

/** Round model v2: readiness of a single branch within the current round. */
export type RoundReadiness =
  | 'ready'        // every current-round question answered or skipped
  | 'incomplete'   // at least one question still unresolved
  | 'no_questions' // active branch with no questions in current round (rare)
  | 'terminated'   // branch is terminated; not in this round
  | 'pruned'       // branch is pruned; not in this round

interface ConversationStore {
  // --- identity ---
  state: ConversationState
  projectId: string | null
  currentRound: number

  // --- situation ---
  rawSituation: string
  cleanedSituation: string
  diff: DiffChange[]
  isCleanedConfirmed: boolean
  // Cleaner vagueness flags (spec answer #1, #15)
  cleanerVaguenessIssues: Array<{
    segment: string
    issue: string
    clarification_needed: string
  }>
  cleanerRequiresClarification: boolean
  // Revision counter — increments every time the Cleaner runs again on
  // a re-submitted (clarified) situation. Starts at 1 on the first run.
  cleanerRevision: number

  // --- branches / messages / Q&A ---
  branches: Branch[]
  messages: Message[]
  activeBranchId: string | null
  activeQuestionId: string | null
  questionsPerBranch: Map<string, Question[]>
  answersPerBranch: Map<string, Map<string, Answer>>

  // --- AAI (legacy global state — kept for backward compat) ---
  aaiState: AAIState
  aaiSuggestion: AAISuggestion | null

  /**
   * Per-question AAI slots. Each entry is keyed by `questionId` and
   * tracks an independent in-flight Assist request. This is what powers
   * parallel AAI: multiple Assist buttons can be clicked at once and
   * each one streams into its own slot without interfering with the
   * others.
   */
  aaiByQuestion: Record<string, AAIQuestionSlot>

  // --- OAI output ---
  problemOutput: ProblemOutput | null

  // --- streaming ---
  isStreaming: boolean
  streamContent: string

  // --- error ---
  error: string | null
  isRecoverable: boolean

  // --- retry tracking ---
  lastAction: { type: string; args?: Record<string, unknown> } | null

  // --- round model v2 ---
  /** Whether a global round submit is currently in flight. */
  roundSubmitting: boolean
  /** Number of new parallel branches spawned in the most recent round. */
  newBranchesThisRound: number
  /**
   * Whether the auto-OAI prompt has already been shown for the current
   * "all branches terminated" condition. Reset when a new round begins.
   */
  autoGenerateModalShown: boolean

  // ================================================================== //
  // Actions
  // ================================================================== //

  // identity
  setProjectId: (id: string) => void
  setState: (state: ConversationState) => void
  advanceRound: () => void

  // situation
  setRawSituation: (situation: string) => void
  setCleanedSituation: (cleaned: string, diff?: DiffChange[]) => void
  setDiff: (diff: DiffChange[]) => void
  setCleanerFlags: (flags: {
    vaguenessIssues?: Array<{
      segment: string
      issue: string
      clarification_needed: string
    }>
    requiresClarification?: boolean
  }) => void
  bumpCleanerRevision: () => void
  resetCleanerRevision: () => void
  confirmCleaned: (useOriginal?: boolean) => void

  // branches
  setBranches: (branches: Branch[]) => void
  addBranch: (branch: Branch) => void
  updateBranch: (branchId: string, updates: Partial<Branch>) => void
  removeBranch: (branchId: string) => void
  setActiveBranch: (branchId: string | null) => void
  terminateBranch: (branchId: string, reason?: string) => void
  pruneBranch: (branchId: string) => void
  renameBranch: (branchId: string, name: string) => void

  // messages
  setMessages: (messages: Message[]) => void
  addMessage: (message: Message) => void

  // questions / answers
  setQuestionsForBranch: (branchId: string, questions: Question[]) => void
  getQuestionsForBranch: (branchId: string) => Question[]
  getAnswersForBranch: (branchId: string) => Map<string, Answer>
  setAnswerForQuestion: (branchId: string, questionId: string, answer: Answer) => void
  clearAnswersForBranch: (branchId: string) => void
  setActiveQuestion: (questionId: string | null) => void

  // AAI (legacy global API — preserved for callers that haven't migrated)
  setAAIState: (partial: Partial<AAIState>) => void
  setAAISuggestion: (suggestion: AAISuggestion | null) => void
  acceptAAISuggestion: () => void
  rejectAAISuggestion: () => void
  editAAISuggestion: (edited: string) => void

  // Per-question AAI slots (parallel Assist)
  /** Replace or initialise the slot for a single question. */
  aaiSlotPatch: (questionId: string, partial: Partial<AAIQuestionSlot>) => void
  /** Append a streamed chunk to the in-progress suggestion. */
  aaiSlotAppendStream: (questionId: string, chunk: string) => void
  /** Drop the slot for a single question (e.g. on accept / dismiss). */
  aaiSlotClear: (questionId: string) => void
  /** Drop every slot — used on hard reset. */
  aaiSlotClearAll: () => void

  // OAI
  setProblemOutput: (output: ProblemOutput | null) => void

  // streaming
  setStreaming: (isStreaming: boolean) => void
  setStreamContent: (content: string) => void
  appendStreamContent: (content: string) => void
  clearStreamContent: () => void

  // error
  setError: (message: string, recoverable?: boolean) => void
  clearError: () => void

  // retry
  setLastAction: (action: { type: string; args?: Record<string, unknown> } | null) => void

  // round model v2
  setCurrentRound: (round: number) => void
  setRoundSubmitting: (busy: boolean) => void
  bumpNewBranchesThisRound: () => void
  resetNewBranchesThisRound: () => void
  setAutoGenerateModalShown: (shown: boolean) => void
  /**
   * Hydrate every branch's draft from the backend on mount. Drafts
   * shadow `answersPerBranch`: a branch's "ready" status is computed
   * over the union of submitted-this-session answers + persisted drafts.
   */
  hydrateDrafts: (
    drafts: Array<{
      branchId: string
      questionId: string
      draftText: string
      draftStatus: AnswerStatus
      selectedOptions?: string[]
      otherText?: string
    }>,
  ) => void

  // reset
  reset: () => void
  resetConversation: () => void

  // computed getters
  getActiveBranch: () => Branch | undefined
  getQuestionsForActiveBranch: () => Question[]
  getAnswersForActiveBranch: () => Map<string, Answer>
  getCompletedBranches: () => Branch[]
  getActiveBranches: () => Branch[]
  canGenerateProblem: () => boolean

  // round model v2 selectors
  /** Readiness of one branch within the current round. */
  getRoundReadiness: (branchId: string) => RoundReadiness
  /** Numerator/denominator answered-or-skipped of a branch this round. */
  getBranchProgress: (branchId: string) => { answered: number; total: number }
  /** True iff every active branch is `ready`. Drives the global Submit. */
  canSubmitRound: () => boolean
}

const initialAAIState: AAIState = {
  isActive: false,
  webSearchInProgress: false,
}

const initialState = {
  state: 'IDLE' as ConversationState,
  projectId: null,
  currentRound: 0,
  rawSituation: '',
  cleanedSituation: '',
  diff: [] as DiffChange[],
  isCleanedConfirmed: false,
  cleanerVaguenessIssues: [] as Array<{
    segment: string
    issue: string
    clarification_needed: string
  }>,
  cleanerRequiresClarification: false,
  cleanerRevision: 1,
  branches: [] as Branch[],
  messages: [] as Message[],
  activeBranchId: null,
  activeQuestionId: null,
  questionsPerBranch: new Map<string, Question[]>(),
  answersPerBranch: new Map<string, Map<string, Answer>>(),
  aaiState: initialAAIState,
  aaiSuggestion: null as AAISuggestion | null,
  aaiByQuestion: {} as Record<string, AAIQuestionSlot>,
  problemOutput: null as ProblemOutput | null,
  isStreaming: false,
  streamContent: '',
  error: null as string | null,
  isRecoverable: true,
  lastAction: null as { type: string; args?: Record<string, unknown> } | null,
  roundSubmitting: false,
  newBranchesThisRound: 0,
  autoGenerateModalShown: false,
}

/**
 * A question is "resolved" for the current round when the user has either
 * skipped it OR provided some content/option. A blank `partial` does not
 * count.
 */
function isAnswerResolved(answer: Answer | undefined): boolean {
  if (!answer) return false
  if (answer.status === 'skipped') return true
  const hasText = (answer.content ?? '').trim().length > 0
  const hasOptions = (answer.selectedOptions?.length ?? 0) > 0
  const hasOther = (answer.otherText ?? '').trim().length > 0
  return hasText || hasOptions || hasOther
}

export const useConversationStore = create<ConversationStore>((set, get) => ({
  ...initialState,

  // ------------------------ identity ------------------------
  setProjectId: (id) => set({ projectId: id }),
  setState: (state) => set({ state }),
  advanceRound: () => set((s) => ({ currentRound: s.currentRound + 1 })),

  // ------------------------ situation ------------------------
  setRawSituation: (situation) => set({ rawSituation: situation }),

  setCleanedSituation: (cleaned, diff) =>
    set((s) => ({
      cleanedSituation: cleaned,
      diff: diff ?? s.diff,
    })),

  setDiff: (diff) => set({ diff }),

  setCleanerFlags: (flags) =>
    set({
      cleanerVaguenessIssues: flags.vaguenessIssues ?? [],
      cleanerRequiresClarification: flags.requiresClarification ?? false,
    }),

  bumpCleanerRevision: () =>
    set((s) => ({ cleanerRevision: s.cleanerRevision + 1 })),

  resetCleanerRevision: () => set({ cleanerRevision: 1 }),

  confirmCleaned: (useOriginal = false) =>
    set((s) => ({
      isCleanedConfirmed: true,
      cleanedSituation: useOriginal ? s.rawSituation : s.cleanedSituation,
      state: 'QUESTIONING',
    })),

  // ------------------------ branches ------------------------
  setBranches: (branches) => set({ branches }),
  addBranch: (branch) => set((s) => ({ branches: [...s.branches, branch] })),
  updateBranch: (branchId, updates) =>
    set((s) => ({
      branches: s.branches.map((b) =>
        b._id === branchId ? { ...b, ...updates } : b
      ),
    })),
  removeBranch: (branchId) =>
    set((s) => ({
      branches: s.branches.filter((b) => b._id !== branchId),
      activeBranchId: s.activeBranchId === branchId ? null : s.activeBranchId,
    })),
  setActiveBranch: (branchId) => set({ activeBranchId: branchId }),
  terminateBranch: (branchId, reason) =>
    set((s) => ({
      branches: s.branches.map((b) =>
        b._id === branchId
          ? { ...b, status: 'terminated_manual' as const, terminationReason: reason }
          : b
      ),
    })),
  pruneBranch: (branchId) =>
    set((s) => ({
      branches: s.branches.map((b) =>
        b._id === branchId ? { ...b, status: 'pruned' as const } : b
      ),
    })),
  renameBranch: (branchId, name) =>
    set((s) => ({
      branches: s.branches.map((b) =>
        b._id === branchId ? { ...b, userRenamedName: name } : b
      ),
    })),

  // ------------------------ messages ------------------------
  setMessages: (messages) => set({ messages }),
  addMessage: (message) => set((s) => ({ messages: [...s.messages, message] })),

  // ------------------------ questions / answers ------------------------
  setQuestionsForBranch: (branchId, questions) =>
    set((s) => {
      const next = new Map(s.questionsPerBranch)
      next.set(branchId, questions)
      return { questionsPerBranch: next }
    }),

  getQuestionsForBranch: (branchId) =>
    get().questionsPerBranch.get(branchId) ?? [],

  getAnswersForBranch: (branchId) =>
    get().answersPerBranch.get(branchId) ?? new Map(),

  setAnswerForQuestion: (branchId, questionId, answer) =>
    set((s) => {
      const next = new Map(s.answersPerBranch)
      const branchAnswers = new Map(next.get(branchId) ?? new Map())
      branchAnswers.set(questionId, answer)
      next.set(branchId, branchAnswers)
      return { answersPerBranch: next }
    }),

  clearAnswersForBranch: (branchId) =>
    set((s) => {
      const next = new Map(s.answersPerBranch)
      next.delete(branchId)
      return { answersPerBranch: next }
    }),

  setActiveQuestion: (questionId) => set({ activeQuestionId: questionId }),

  // ------------------------ AAI ------------------------
  setAAIState: (partial) =>
    set((s) => ({ aaiState: { ...s.aaiState, ...partial } })),

  setAAISuggestion: (suggestion) => set({ aaiSuggestion: suggestion }),

  acceptAAISuggestion: () => {
    const { aaiSuggestion, activeQuestionId, activeBranchId } = get()
    if (!aaiSuggestion || !activeQuestionId || !activeBranchId) return
    set((s) => {
      const next = new Map(s.answersPerBranch)
      const branchAnswers = new Map(next.get(activeBranchId) ?? new Map())
      branchAnswers.set(activeQuestionId, {
        questionId: activeQuestionId,
        status: 'answered',
        content: aaiSuggestion.suggestion,
        aaiSuggestionUsed: true,
        aaiSuggestionEdited: false,
      })
      next.set(activeBranchId, branchAnswers)
      return {
        answersPerBranch: next,
        activeQuestionId: null,
        aaiSuggestion: null,
        aaiState: { ...s.aaiState, isActive: false },
        state: 'ANSWERING' as ConversationState,
      }
    })
  },

  rejectAAISuggestion: () =>
    set((s) => ({
      activeQuestionId: null,
      aaiSuggestion: null,
      aaiState: { ...s.aaiState, isActive: false },
      state: 'ANSWERING',
    })),

  editAAISuggestion: (edited) => {
    const { activeQuestionId, activeBranchId } = get()
    if (!activeQuestionId || !activeBranchId) return
    set((s) => {
      const next = new Map(s.answersPerBranch)
      const branchAnswers = new Map(next.get(activeBranchId) ?? new Map())
      branchAnswers.set(activeQuestionId, {
        questionId: activeQuestionId,
        status: 'answered',
        content: edited,
        aaiSuggestionUsed: true,
        aaiSuggestionEdited: true,
      })
      next.set(activeBranchId, branchAnswers)
      return {
        answersPerBranch: next,
        activeQuestionId: null,
        aaiSuggestion: null,
        aaiState: { ...s.aaiState, isActive: false },
        state: 'ANSWERING' as ConversationState,
      }
    })
  },

  // ---------------- per-question AAI slots (parallel Assist) ----------------
  aaiSlotPatch: (questionId, partial) =>
    set((s) => {
      const prev: AAIQuestionSlot =
        s.aaiByQuestion[questionId] ?? {
          isActive: false,
          webSearchInProgress: false,
          streamingContent: '',
          suggestion: null,
        }
      return {
        aaiByQuestion: {
          ...s.aaiByQuestion,
          [questionId]: { ...prev, ...partial },
        },
      }
    }),

  aaiSlotAppendStream: (questionId, chunk) =>
    set((s) => {
      const prev: AAIQuestionSlot =
        s.aaiByQuestion[questionId] ?? {
          isActive: true,
          webSearchInProgress: false,
          streamingContent: '',
          suggestion: null,
        }
      return {
        aaiByQuestion: {
          ...s.aaiByQuestion,
          [questionId]: {
            ...prev,
            streamingContent: prev.streamingContent + chunk,
          },
        },
      }
    }),

  aaiSlotClear: (questionId) =>
    set((s) => {
      if (!(questionId in s.aaiByQuestion)) return s
      const next = { ...s.aaiByQuestion }
      delete next[questionId]
      return { aaiByQuestion: next }
    }),

  aaiSlotClearAll: () => set({ aaiByQuestion: {} }),

  // ------------------------ OAI ------------------------
  setProblemOutput: (output) => set({ problemOutput: output }),

  // ------------------------ streaming ------------------------
  setStreaming: (isStreaming) => set({ isStreaming }),
  setStreamContent: (content) => set({ streamContent: content }),
  appendStreamContent: (content) =>
    set((s) => ({ streamContent: s.streamContent + content })),
  clearStreamContent: () => set({ streamContent: '' }),

  // ------------------------ error ------------------------
  setError: (message, recoverable = true) =>
    set({ error: message, isRecoverable: recoverable, state: 'ERROR' }),
  clearError: () => set({ error: null, isRecoverable: true }),

  // ------------------------ retry tracking ------------------------
  setLastAction: (action) => set({ lastAction: action }),

  // ------------------------ round model v2 ------------------------
  setCurrentRound: (round) => set({ currentRound: round }),
  setRoundSubmitting: (busy) => set({ roundSubmitting: busy }),
  bumpNewBranchesThisRound: () =>
    set((s) => ({ newBranchesThisRound: s.newBranchesThisRound + 1 })),
  resetNewBranchesThisRound: () => set({ newBranchesThisRound: 0 }),
  setAutoGenerateModalShown: (shown) => set({ autoGenerateModalShown: shown }),

  hydrateDrafts: (drafts) =>
    set((s) => {
      const next = new Map(s.answersPerBranch)
      for (const d of drafts) {
        const branchAnswers = new Map(next.get(d.branchId) ?? new Map())
        // Don't clobber an in-memory answer that's newer than the draft
        // (the in-memory one was edited locally since hydrate started).
        if (branchAnswers.has(d.questionId)) continue
        const status: AnswerStatus =
          d.draftStatus === 'answered' || d.draftStatus === 'skipped'
            ? d.draftStatus
            : 'partial'
        branchAnswers.set(d.questionId, {
          questionId: d.questionId,
          status,
          content: d.draftText,
          selectedOptions: d.selectedOptions,
          otherText: d.otherText,
        })
        next.set(d.branchId, branchAnswers)
      }
      return { answersPerBranch: next }
    }),

  // ------------------------ reset ------------------------
  reset: () => set(initialState),
  resetConversation: () => set(initialState),

  // ------------------------ computed getters ------------------------
  getActiveBranch: () => {
    const { branches, activeBranchId } = get()
    return branches.find((b) => b._id === activeBranchId)
  },

  getQuestionsForActiveBranch: () => {
    const { questionsPerBranch, activeBranchId } = get()
    if (!activeBranchId) return []
    return questionsPerBranch.get(activeBranchId) ?? []
  },

  getAnswersForActiveBranch: () => {
    const { answersPerBranch, activeBranchId } = get()
    if (!activeBranchId) return new Map()
    return answersPerBranch.get(activeBranchId) ?? new Map()
  },

  getCompletedBranches: () => {
    const { branches } = get()
    return branches.filter(
      (b) =>
        b.status === 'terminated_natural' || b.status === 'terminated_manual'
    )
  },

  getActiveBranches: () => {
    const { branches } = get()
    return branches.filter((b) => b.status === 'active')
  },

  canGenerateProblem: () => {
    const { branches, state } = get()
    const active = branches.filter((b) => b.status === 'active')
    return (
      active.length === 0 &&
      branches.length > 0 &&
      state !== 'GENERATING' &&
      state !== 'ERROR'
    )
  },

  // ------------------------ round model v2 selectors ------------------------
  getRoundReadiness: (branchId) => {
    const { branches, questionsPerBranch, answersPerBranch } = get()
    const branch = branches.find((b) => b._id === branchId)
    if (!branch) return 'incomplete'
    if (branch.status === 'pruned') return 'pruned'
    if (
      branch.status === 'terminated_natural' ||
      branch.status === 'terminated_manual'
    ) {
      return 'terminated'
    }
    const questions = questionsPerBranch.get(branchId) ?? []
    if (questions.length === 0) return 'no_questions'
    const answers = answersPerBranch.get(branchId) ?? new Map()
    for (const q of questions) {
      if (!isAnswerResolved(answers.get(q.id))) return 'incomplete'
    }
    return 'ready'
  },

  getBranchProgress: (branchId) => {
    const { questionsPerBranch, answersPerBranch } = get()
    const questions = questionsPerBranch.get(branchId) ?? []
    const answers = answersPerBranch.get(branchId) ?? new Map()
    const answered = questions.reduce(
      (acc, q) => (isAnswerResolved(answers.get(q.id)) ? acc + 1 : acc),
      0,
    )
    return { answered, total: questions.length }
  },

  canSubmitRound: () => {
    const { branches, roundSubmitting } = get()
    if (roundSubmitting) return false
    const active = branches.filter((b) => b.status === 'active')
    if (active.length === 0) return false
    const readiness = get().getRoundReadiness
    return active.every(
      (b) => readiness(b._id) === 'ready' || readiness(b._id) === 'no_questions',
    )
  },
}))
